import Store from "store";
import {
  AUTH_TOKEN,
  ENV_MAPPER,
  NDC_CONNECT_ENDPOINT,
  REFRESH_TOKEN,
  SELECTED_ORGANIZATION,
} from "../utils/constants";
import {
  decodeReadStringifyObj,
  generateRedirectAppUrl,
  getEnv,
  getSessionStorage,
  isEmpty,
  setSessionStorage,
} from "../utils/utils";
import { createStore } from "easy-peasy";
import { Cookies } from "react-cookie";

let refreshHandler: (() => Promise<boolean>) | null = null;
let refreshPromise: Promise<string | null> | null = null;
export const store = createStore(Store);
const actions: any = store.getActions();
const cookie = new Cookies();

export const idpRefreshMutation = `
  mutation idpRefresh {
    idpRefresh {
      accessToken
      tokenType
      expiresIn
    }
  }
`;

export const idpJwtTokenQuery = `
  query idpJwtToken {
    idpJwtToken {
      jwtToken
    }
  }
`;

export const idpLogoutQuery = `
  mutation IdpLogout {
    idpLogout {
      ok
    }
  }
`;

export type IdpTokenResponse = {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
};

export function setAuthRefreshHandler(
  handler: (() => Promise<boolean>) | null
): void {
  refreshHandler = handler;
}

export function getGraphQLBase(): string {
  const value =
    process.env.REACT_APP_NDC_BASE_URL ||
    process.env.REACT_APP_BASE_URL ||
    "http://localhost:8080";
  return value.replace(/\/$/, "");
}

export function getGraphQLEndpoint(
  path: string = NDC_CONNECT_ENDPOINT
): string {
  return `${getGraphQLBase()}/${path}`;
}

export async function refreshAccessToken(): Promise<string | null> {
  const sessionAuthToken = window.sessionStorage.getItem(AUTH_TOKEN);
  if (!refreshPromise && sessionAuthToken) {
    const actions: any = store.getActions();
    refreshPromise = (async () => {
      try {
        const idpRefreshData = await graphqlRequest<{
          idpRefresh: IdpTokenResponse;
        }>(idpRefreshMutation, undefined, undefined, NDC_CONNECT_ENDPOINT);
        const accessToken: any = idpRefreshData.idpRefresh.accessToken;

        const idpJwtTokenData = await graphqlRequest<{
          idpJwtToken: any;
        }>(idpJwtTokenQuery, undefined, accessToken, NDC_CONNECT_ENDPOINT);
        const newAccessToken = idpJwtTokenData?.idpJwtToken?.jwtToken;

        actions.auth.updateAuthToken(newAccessToken);
        setSessionStorage(AUTH_TOKEN, newAccessToken);

        return newAccessToken;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
}

async function executeGraphQL(
  query: string,
  variables?: Record<string, unknown>,
  accessToken?: string | null,
  endpoint?: string
) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const authToken = accessToken || getSessionStorage(AUTH_TOKEN);

  if (authToken) {
    // headers["Authorization"] = `Bearer ${authToken}`;
  }

  const selectedOrganisation = decodeReadStringifyObj(SELECTED_ORGANIZATION);
  headers["X-nf-active-org-short-code"] = selectedOrganisation?.group || "";

  return fetch(getGraphQLEndpoint(endpoint), {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      query,
      variables,
    }),
  });
}

export async function graphqlRequest<T>(
  query: string,
  variables?: Record<string, unknown>,
  accessToken?: string | null,
  endpoint?: string
): Promise<T> {
  let response = await executeGraphQL(query, variables, accessToken, endpoint);
  const json = await response.json();
  const unauthorized = json?.errors?.some(
    (err: any) =>
      err.extensions?.code === "UNAUTHENTICATED" ||
      err.message?.includes("token") ||
      err.message?.includes("expired") ||
      err.message?.includes("unauthorized") ||
      err.message?.includes("Authentication credentials were not provided")
  );

  if (unauthorized) {
    if (json?.errors?.[0]?.path?.[0] === "idpRefresh") {
      throw new Error("Session expired");
    }
    logout();
  }

  return json.data;
}

// export async function graphqlRequest<T>(
//   query: string,
//   variables?: Record<string, unknown>,
//   accessToken?: string,
//   endpoint?: string
// ): Promise<T> {
//   const headers: Record<string, string> = {
//     "Content-Type": "application/json",
//   };

//   const authToken = accessToken || getSessionStorage(AUTH_TOKEN);
//   const selectedOrganisation = decodeReadStringifyObj(SELECTED_ORGANIZATION);
//   headers["X-nf-active-org-short-code"] = selectedOrganisation?.group || "";
//   if (authToken) {
//     const tokenPrefix = endpoint === "core/graphql" ? "Bearer" : "Bearer";
//     // headers["Authorization"] = `${tokenPrefix} ${authToken}`;
//   }

//   const response = await fetch(getGraphQLEndpoint(endpoint), {
//     method: "POST",
//     headers,
//     credentials: "include",
//     body: JSON.stringify({ query, variables }),
//   });

//   if (!response.ok) {
//     const text = await response.text();
//     let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
//     try {
//       const json = JSON.parse(text);
//       if (json.errors && json.errors[0]) {
//         errorMessage = json.errors[0].message || errorMessage;
//       } else if (json.error) {
//         errorMessage = json.error;
//       } else if (json.detail) {
//         errorMessage = json.detail;
//       }
//     } catch {
//       if (text) {
//         errorMessage = text;
//       }
//     }
//     throw new Error(errorMessage);
//   }

//   const json = await response.json();
//   if (json.errors) {
//     throw new Error(json.errors[0].message);
//   }
//   return json.data;
// }

function isAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes("401") ||
    message.includes("unauthorized") ||
    message.includes("signature has expired") ||
    message.includes("token") ||
    message.includes("jwt")
  );
}

export async function graphqlRequestWithAuthRetry<T>(
  query: string,
  variables?: Record<string, unknown>,
  accessToken?: string,
  endpoint?: string
): Promise<T> {
  try {
    return await graphqlRequest<T>(query, variables, accessToken, endpoint);
  } catch (error) {
    if (!isAuthError(error) || !refreshHandler) {
      throw error;
    }

    const refreshed = await refreshHandler();
    if (!refreshed) {
      throw error;
    }

    return graphqlRequest<T>(query, variables, accessToken, endpoint);
  }
}

export async function logout() {
  try {
    const query = idpLogoutQuery;
    const logout: any = await graphqlRequest(
      query,
      undefined,
      undefined,
      NDC_CONNECT_ENDPOINT
    );

    if (logout?.idpLogout?.ok) {
      const actions: any = store.getActions();
      actions.auth.clearAuthToken();
      actions.auth.clearRefreshToken();
      sessionStorage.clear();
      localStorage.clear();
      cookie.remove(AUTH_TOKEN, { path: "/" });
      cookie.remove(REFRESH_TOKEN, { path: "/" });
    }
  } finally {
    if (getEnv() !== ENV_MAPPER.local) {
      window.location.href = generateRedirectAppUrl("home");
    }
  }
}
